/** World metres onto the design's stage.
 *
 * The mock draws the spectate map inside a fixed 400×260 viewBox and states
 * every weight in that box's units: a 24-unit bed, a 1-unit centre line, a
 * 12-unit halo round the car. Keeping that box and mapping the circuit into
 * it — rather than fitting the viewBox to the geometry, as the Tracks card
 * does — means those numbers survive contact with a real track: Tsukuba and
 * the Nordschleife both arrive as a 400×260 picture with the same bed width,
 * instead of one of them rendering a hairline and the other a slab.
 *
 * The mapping is uniform and centred, so the circuit keeps its shape, and `z`
 * is negated on the way in because SVG counts y downwards while the game's
 * map does not. That is the same convention `components/TrackOutline.tsx`
 * uses, and the two drawings should read the same way up.
 *
 * What the bed is drawn from is decided here too, in order of honesty: the
 * compiled centreline if the track-data repo has published one, otherwise
 * every surveyed border run, otherwise the path the car itself has taken
 * since this page connected. Only when there is none of that does the
 * design's stylised loop appear, and then without a car on it — a dot placed
 * on a decorative shape would be claiming to know something.
 */

import type { Polyline, TrackGeometry } from '@/lib/types';

/** The mock's viewBox, kept so its weights stay meaningful. */
export const STAGE_WIDTH = 400;
export const STAGE_HEIGHT = 260;

/** Room round the circuit, in stage units, for the car's halo and the S/F
 *  label. */
export const STAGE_PAD = 30;

/** A point in world metres: `[x, z]`. */
export type World = readonly [number, number];

/** A point on the stage: `[x, y]`, y already flipped. */
export type Stage = [number, number];

export interface Bounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

export interface Projection {
  scale: number;
  /** World coordinates of the stage's centre. */
  centreX: number;
  centreZ: number;
}

export function boundsOf(points: Iterable<World>): Bounds | null {
  let bounds: Bounds | null = null;
  for (const point of points) {
    const x = point[0];
    const z = point[1];
    if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
    if (!bounds) {
      bounds = { minX: x, minZ: z, maxX: x, maxZ: z };
      continue;
    }
    if (x < bounds.minX) bounds.minX = x;
    if (z < bounds.minZ) bounds.minZ = z;
    if (x > bounds.maxX) bounds.maxX = x;
    if (z > bounds.maxZ) bounds.maxZ = z;
  }
  return bounds;
}

export function unionBounds(a: Bounds | null, b: Bounds | null): Bounds | null {
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX),
    minZ: Math.min(a.minZ, b.minZ),
    maxX: Math.max(a.maxX, b.maxX),
    maxZ: Math.max(a.maxZ, b.maxZ),
  };
}

/** How wide the circuit is, at its widest. Used to decide whether a trail is
 *  yet worth drawing a map from. */
export function spanOf(bounds: Bounds | null): number {
  if (!bounds) return 0;
  return Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ);
}

/** The uniform, centred fit. A degenerate box — one point, or a straight
 *  line — is given a minimum size so the scale is never infinite. */
export function projectionFor(bounds: Bounds, pad: number = STAGE_PAD): Projection {
  const width = Math.max(bounds.maxX - bounds.minX, 1);
  const height = Math.max(bounds.maxZ - bounds.minZ, 1);
  const scale = Math.min((STAGE_WIDTH - pad * 2) / width, (STAGE_HEIGHT - pad * 2) / height);
  return {
    scale,
    centreX: (bounds.minX + bounds.maxX) / 2,
    centreZ: (bounds.minZ + bounds.maxZ) / 2,
  };
}

export function project(projection: Projection, x: number, z: number): Stage {
  return [
    (x - projection.centreX) * projection.scale + STAGE_WIDTH / 2,
    -(z - projection.centreZ) * projection.scale + STAGE_HEIGHT / 2,
  ];
}

/** `M x y L x y …`, to two decimals. Open: a border run that does not close
 *  should not be drawn as though it did. */
export function pathOf(projection: Projection, points: readonly World[]): string {
  const drawn: string[] = [];
  for (const point of points) {
    if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) continue;
    const [x, y] = project(projection, point[0], point[1]);
    drawn.push(`${drawn.length === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  return drawn.length >= 2 ? drawn.join(' ') : '';
}

// ── reading the compiled document ─────────────────────────────────────────

function fromVertices(run: Polyline): World[] {
  return run.map((vertex) => [vertex[0] ?? 0, vertex[1] ?? 0] as World);
}

/** The first two numbers of each vertex. One that is not two numbers is
 *  skipped rather than read as a zero, which would put a point at the origin. */
function readVertices(run: readonly unknown[]): World[] {
  const points: World[] = [];
  for (const vertex of run) {
    if (!Array.isArray(vertex)) continue;
    const x: unknown = vertex[0];
    const z: unknown = vertex[1];
    if (typeof x !== 'number' || typeof z !== 'number') continue;
    points.push([x, z]);
  }
  return points;
}

/**
 * The centreline arrives as runs of `[x, z, y, w]` per metre — one run per
 * stretch where both borders are surveyed, broken wherever either is not —
 * and it is read as runs. It was once read as one flat list of vertices,
 * which found an array where it expected a number at every vertex, kept
 * nothing, and let the bed fall back to a border without saying so. A flat
 * list is still accepted as a single run, so neither spelling draws nothing.
 */
function fromCentreline(centreline: readonly unknown[]): World[][] {
  const flat = centreline.some((item) => Array.isArray(item) && typeof item[0] === 'number');
  if (flat) return [readVertices(centreline)];
  return centreline.filter(Array.isArray).map((run) => readVertices(run));
}

function borderRuns(geometry: TrackGeometry): World[][] {
  const runs = [...(geometry.borders?.L ?? []), ...(geometry.borders?.R ?? [])];
  return runs.map(fromVertices).filter((run) => run.length >= 2);
}

/** Every point the circuit is known to occupy, for the fit. */
export function geometryBounds(geometry: TrackGeometry | null | undefined): Bounds | null {
  if (!geometry) return null;
  let bounds: Bounds | null = null;
  for (const run of borderRuns(geometry)) bounds = unionBounds(bounds, boundsOf(run));
  if (geometry.centerline) {
    for (const run of fromCentreline(geometry.centerline)) bounds = unionBounds(bounds, boundsOf(run));
  }
  if (geometry.finish) {
    const [x1, z1, x2, z2] = geometry.finish;
    bounds = unionBounds(
      bounds,
      boundsOf([
        [x1, z1],
        [x2, z2],
      ]),
    );
  }
  return bounds;
}

/**
 * The ribbons the bed is drawn along, one subpath each.
 *
 * The compiled centreline is the road's middle and is exactly what a thick
 * stroke should follow. It comes as one run per stretch where both borders
 * are known, and each run is drawn on its own, so a survey gap stays a gap
 * rather than becoming a straight line across the infield. Without a
 * centreline every surveyed border run stands in, both sides: each is off by
 * half a road width, which at the scale of a whole circuit is less than the
 * stroke, and the two sides merge into one ribbon under it. Every run rather
 * than the longest, because a partly surveyed circuit is the fragments that
 * were driven, and the longest of them is not the circuit.
 */
export function bedRunsOf(geometry: TrackGeometry | null | undefined): World[][] {
  if (!geometry) return [];
  const centre = geometry.centerline
    ? fromCentreline(geometry.centerline).filter((run) => run.length >= 2)
    : [];
  if (centre.length > 0) return centre;
  return borderRuns(geometry);
}

/** One `d` with a subpath per run, so nothing is drawn between runs. */
export function pathOfRuns(projection: Projection, runs: readonly (readonly World[])[]): string {
  return runs
    .map((run) => pathOf(projection, run))
    .filter(Boolean)
    .join(' ');
}

/** The start/finish line, ready to draw. */
export function finishOf(
  projection: Projection,
  geometry: TrackGeometry | null | undefined,
): [Stage, Stage] | null {
  const segment = geometry?.finish;
  if (!segment) return null;
  const [x1, z1, x2, z2] = segment;
  if (![x1, z1, x2, z2].every((value) => Number.isFinite(value))) return null;
  return [project(projection, x1, z1), project(projection, x2, z2)];
}

/** Enough trail to be a map rather than a scribble: a few frames, and far
 *  enough apart that the fit is not all noise. */
export const TRAIL_MIN_POINTS = 6;
export const TRAIL_MIN_SPAN_M = 25;

export function trailIsDrawable(points: readonly World[], bounds: Bounds | null): boolean {
  return points.length >= TRAIL_MIN_POINTS && spanOf(bounds) >= TRAIL_MIN_SPAN_M;
}
